import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../components/Box";
import { Helpers } from "../../../helpers/Helpers";
import { KTCard } from "../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../_metronic/layout/core";
import { storeActivityIndicator } from "../../../redux/reducers/authSlice";
import CustomSingleSelect, { CustomSingleSelectModel } from "../../../components/CustomSingleSelect";
import CustomMultiSelect, { CustomMultiSelectModel } from "../../../components/CustomMultiSelect";
import CustomNumberInput from "../../../components/CustomNumberInput";
import CustomTextInput from "../../../components/CustomTextInput";
import CustomDatePicker from "../../../components/CustomDatePicker";
import CustomSwitch from "../../../components/CustomSwitch";
import MailListesiAlani from "../../../components/MailListesiAlani";
import { CompanyService } from "../../../services/CompanyService";
import {
    CRON_SABLONLARI, DONEMLER, FATURA_TARIHLERI, GONDERIMLER, GRUPLAMA_ALANLARI, KURAL_TURLERI, OTOMATIK_FATURA_SAYFASI,
    OtomatikFaturaKuralKaydet, OtomatikFaturaService, SABIT_GELIR_SECENEKLERI, tarihVeyaBos
} from "../../../services/OtomatikFaturaService";
import OnizlemeModal from "./Components/OnizlemeModal";

const secenek = (liste: { value: number | string, label: string }[], deger: number | string | undefined | null): CustomSingleSelectModel | undefined => {
    const s = liste.find(x => String(x.value) == String(deger ?? ''));
    return s ? { label: s.label, value: String(s.value) } : undefined;
}

const secenekler = (liste: { value: number | string, label: string }[]) =>
    () => Promise.resolve({ isSuccess: true, data: liste.map(x => ({ label: x.label, value: String(x.value) })) });

/**
 * Ayarlar > Otomatik Faturalama > kural (22.10.2026). Bölümler: genel, zamanlama (cron + hazır seçenekler + sonraki 5
 * çalışma), kapsam, gönderim, onay ve bildirim. Altta kuralın sade dille özeti; Önizle kayıt oluşturmadan oluşacak
 * faturaları gösterir, aynı pencereden dönem "şimdi" çalıştırılır.
 */
const OtomatikFaturaAdd = () => {
    const navigate = useNavigate();
    const pageName = OTOMATIK_FATURA_SAYFASI
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const [kuralId, setKuralId] = useState<string | undefined>(undefined);
    const [ad, setAd] = useState<string | undefined>(undefined);
    const [kuralTuru, setKuralTuru] = useState<CustomSingleSelectModel | undefined>(secenek(KURAL_TURLERI, 1));
    const [aktif, setAktif] = useState<boolean>(true);
    const [cron, setCron] = useState<string | undefined>('0 0 6 1 * ?');
    const [baslangic, setBaslangic] = useState<string | undefined>(new Date().toISOString().slice(0, 10));
    const [bitis, setBitis] = useState<string | undefined>(undefined);
    const [donem, setDonem] = useState<CustomSingleSelectModel | undefined>(secenek(DONEMLER, 1));
    const [faturaTarihi, setFaturaTarihi] = useState<CustomSingleSelectModel | undefined>(secenek(FATURA_TARIHLERI, 1));
    const [musteriler, setMusteriler] = useState<CustomMultiSelectModel[]>([]);
    const [poNumaralari, setPoNumaralari] = useState<CustomMultiSelectModel[]>([]);
    const [sabitGelirTuru, setSabitGelirTuru] = useState<CustomSingleSelectModel | undefined>(secenek(SABIT_GELIR_SECENEKLERI, ''));
    const [gruplama, setGruplama] = useState<string[]>(['PONO']);
    const [hizmetGrupla, setHizmetGrupla] = useState<boolean>(true);
    const [aySonundaBol, setAySonundaBol] = useState<boolean>(false);
    const [gonderim, setGonderim] = useState<number>(2);
    const [onayGerekli, setOnayGerekli] = useState<boolean>(true);
    const [onaylayici, setOnaylayici] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [hatirlatmaDakika, setHatirlatmaDakika] = useState<string | undefined>('60');
    const [hatirlatmaBas, setHatirlatmaBas] = useState<string | undefined>('8');
    const [hatirlatmaBit, setHatirlatmaBit] = useState<string | undefined>('20');
    const [bilgiAlicilari, setBilgiAlicilari] = useState<string | undefined>(undefined);

    const [cronOnizleme, setCronOnizleme] = useState<{ gecerli: boolean, zamanlar: string[] }>({ gecerli: true, zamanlar: [] });
    const [onizlemeAcik, setOnizlemeAcik] = useState<boolean>(false);
    const [degisti, setDegisti] = useState<boolean>(false);

    const tur = Number(kuralTuru?.value ?? 1);
    // nakliye: aylık sabit gelir (sipariş + fatura). Nakliye dönemsel ve Yurtiçi dönemsel gruplamalı türlerdir.
    const nakliye = tur == 1;
    const birim = tur == 3 ? 'Nakliye siparişi' : 'kargo';
    const projeAdi = tur == 3 ? 'Nakliye' : 'Yurtiçi Kargo';

    useEffect(() => {
        var id = searchParams.get('id');

        if (id)
            getById(id);
    }, [])

    // Kayıttan sonraki her değişiklik "önce kaydedin" uyarısı için işaretlenir (önizleme kayıtlı kurala göre hesaplanır).
    const ilkYukleme = useRef(true);
    useEffect(() => {
        if (ilkYukleme.current) {
            ilkYukleme.current = false;
            return;
        }
        setDegisti(true);
    }, [ad, kuralTuru, aktif, cron, baslangic, bitis, donem, faturaTarihi, musteriler, sabitGelirTuru, gruplama, hizmetGrupla, aySonundaBol, poNumaralari, gonderim, onayGerekli, onaylayici, hatirlatmaDakika, hatirlatmaBas, hatirlatmaBit, bilgiAlicilari])

    // Sabit gelir aylıktır: dönem "Önceki ay".
    useEffect(() => {
        if (nakliye && donem?.value != '1')
            setDonem(secenek(DONEMLER, 1));
    }, [nakliye])

    // Cron yazılırken sonraki çalışma zamanları (yarım saniye bekleyerek).
    useEffect(() => {
        const zamanlayici = setTimeout(async () => {
            try {
                var response = await OtomatikFaturaService.cronOnizle(cron, baslangic, bitis);

                if (response.isSuccess)
                    setCronOnizleme(response.data);
            } catch { }
        }, 500);

        return () => clearTimeout(zamanlayici);
    }, [cron, baslangic, bitis])

    const getById = async (_id: string) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await OtomatikFaturaService.getById(_id);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            var k = response.data;

            ilkYukleme.current = true;
            setKuralId(k.otomatikFaturaKuraliId);
            setAd(k.ad);
            setKuralTuru(secenek(KURAL_TURLERI, k.kuralTuru));
            setAktif(k.isCustomActive);
            setCron(k.cron);
            setBaslangic(k.baslangicTarihi ?? undefined);
            setBitis(k.bitisTarihi ?? undefined);
            setDonem(secenek(DONEMLER, k.donem));
            setFaturaTarihi(secenek(FATURA_TARIHLERI, k.faturaTarihi));
            setSabitGelirTuru(secenek(SABIT_GELIR_SECENEKLERI, k.sabitGelirTuru ?? ''));
            setGruplama(k.gruplama ?? []);
            setHizmetGrupla(k.hizmetGrupla);
            setAySonundaBol(!!k.aySonundaBol);
            setGonderim(k.gonderim);
            setOnayGerekli(k.onayGerekli);
            setOnaylayici(k.onaylayiciUserId ? { label: k.onaylayiciAdi ?? '', value: k.onaylayiciUserId } : undefined);
            setHatirlatmaDakika(String(k.hatirlatmaDakika));
            setHatirlatmaBas(String(k.hatirlatmaBaslangicSaati));
            setHatirlatmaBit(String(k.hatirlatmaBitisSaati));
            setBilgiAlicilari(k.bilgiAlicilari ?? undefined);

            // Müşteri adları listede "A, B" olarak gelir; seçim kutusuna Id + ad olarak yerleşir.
            var adlar = (k.musteriler && k.musteriler != 'Tümü') ? k.musteriler.split(', ') : [];
            setMusteriler((k.musteriIdleri ?? []).map((m: string, i: number) => ({ label: adlar[i] ?? m, value: m })));
            setPoNumaralari((k.poNumaralari ?? []).map((p: string) => ({ label: p, value: p })));
            setTimeout(() => setDegisti(false), 0);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const istek = (): OtomatikFaturaKuralKaydet => ({
        id: kuralId,
        ad: ad,
        kuralTuru: tur,
        isCustomActive: aktif,
        cron: cron?.trim(),
        baslangicTarihi: tarihVeyaBos(baslangic),
        bitisTarihi: tarihVeyaBos(bitis),
        donem: Number(donem?.value ?? 1),
        faturaTarihi: Number(faturaTarihi?.value ?? 1),
        musteriIdleri: musteriler.map(x => x.value),
        poNumaralari: poNumaralari.map(x => x.value),
        sabitGelirTuru: nakliye && sabitGelirTuru?.value ? Number(sabitGelirTuru.value) : undefined,
        gruplama: nakliye ? [] : gruplama,
        hizmetGrupla: !nakliye && hizmetGrupla,
        aySonundaBol: !nakliye && aySonundaBol,
        gonderim: gonderim,
        onayGerekli: onayGerekli,
        onaylayiciUserId: onayGerekli ? onaylayici?.value : undefined,
        hatirlatmaDakika: Number(hatirlatmaDakika ?? 60),
        hatirlatmaBaslangicSaati: Number(hatirlatmaBas ?? 8),
        hatirlatmaBitisSaati: Number(hatirlatmaBit ?? 20),
        bilgiAlicilari: bilgiAlicilari
    })

    const kaydet = async () => {
        if (Helpers.isEmpty(ad)) {
            toast.error('Kural adını giriniz!');
            return;
        }

        if (!cronOnizleme.gecerli) {
            toast.error('Zamanlama (cron) geçerli değil.');
            return;
        }

        if (onayGerekli && gonderim == 1) {
            toast.error("Onaylı kural için Uyumsoft gönderimi gerekir (önizleme PDF'i Uyumsoft taslağından gelir).");
            return;
        }

        if (!tarihVeyaBos(baslangic)) {
            toast.error('Geçerlilik başlangıç tarihini giriniz!');
            return;
        }

        if (onayGerekli && !onaylayici) {
            toast.error('Onaylayıcıyı seçiniz!');
            return;
        }

        try {
            dispatch(storeActivityIndicator(true));

            var response = await OtomatikFaturaService.kaydet(istek());

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            toast.success('Kural kaydedildi.');
            ilkYukleme.current = true;
            setKuralId(response.data.otomatikFaturaKuraliId);
            setTimeout(() => setDegisti(false), 0);

            if (!kuralId)
                navigate('/otomatikfatura/listadd?id=' + response.data.otomatikFaturaKuraliId, { replace: true });
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const onizlemeyiAc = () => {
        if (!kuralId) {
            toast.error('Önizleme kayıtlı kurala göre hesaplanır; önce kaydediniz.');
            return;
        }

        if (degisti) {
            toast.error('Kaydedilmemiş değişiklik var; önizleme kayıtlı kurala göre hesaplanır. Önce kaydediniz.');
            return;
        }

        setOnizlemeAcik(true);
    }

    const ozet = () => {
        if (!aktif)
            return <span><b>Kural pasif.</b> Ayarlar saklanır, çalışmaz.</span>;

        const ilk = cronOnizleme.zamanlar[0];
        const gonderimMetni = gonderim == 3 ? "GİB'e gönderilir" : gonderim == 2 ? "Uyumsoft'a taslak gönderilir" : "Keylogx'te taslak kalır";
        const neyi = nakliye
            ? `${(donem?.label ?? '').toLowerCase()} için sabit gelir siparişlerini ve her sipariş için bir satış faturası oluşturur`
            : `${(donem?.label ?? '').toLowerCase()} içindeki faturalanmamış ${tur == 3 ? 'Nakliye sipariş' : 'Yurtiçi Kargo'} gelirlerini ${['Müşteri', ...GRUPLAMA_ALANLARI.filter(x => gruplama.includes(x.value)).map(x => x.label)].join(' + ')} bazında faturalar`;

        return (
            <span>
                <b>{ilk ? `İlk çalışma ${ilk}.` : 'Zamanlama geçersiz ya da geçerlilik aralığında çalışma yok.'}</b>{' '}
                Kural {neyi}; {onayGerekli
                    ? `faturalar önce ${onaylayici?.label?.split(' (')[0] ?? 'onaylayıcı'} onayına düşer, onaylanınca ${gonderim == 3 ? "GİB'e gönderilir" : "Uyumsoft'ta taslak kalır"}.`
                    : `onay beklenmez, ${gonderimMetni}.`}
                {' '}Fatura tarihi: {(faturaTarihi?.label ?? '').toLowerCase()}.{!nakliye && aySonundaBol ? ' Hafta iki aya yayılırsa her ay ayrı faturalanır; önceki ayın faturası ayın son günü tarihli.' : ''}{bitis ? ` Son geçerli gün ${bitis.split('-').reverse().join('.')}.` : ' Bitiş tarihi yok.'}
            </span>
        );
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    <KTCard className="ebbe-card">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Genel</h4>

                            <div className="row">
                                <div className="col-md-5">
                                    <CustomTextInput labelText="Kural Adı" isLabelRequired value={ad} setValue={setAd} />
                                </div>

                                <div className="col-md-5">
                                    <CustomSingleSelect
                                        labelText="Kural Türü"
                                        isLabelRequired
                                        selectedValue={kuralTuru}
                                        onChange={(v) => setKuralTuru(v ?? secenek(KURAL_TURLERI, 1))}
                                        onMenuOpen={secenekler(KURAL_TURLERI)}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>

                                <div className="col-md-2">
                                    <label className="form-label">Aktif</label>
                                    <CustomSwitch isActive={aktif} setIsActive={setAktif} />
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Zamanlama</h4>

                            <div className="row">
                                <div className="col-md-5">
                                    <CustomTextInput labelText="Cron (saniye dakika saat gün ay haftanın günü)" isLabelRequired value={cron} setValue={setCron} />
                                    {!cronOnizleme.gecerli && <div className="text-danger fs-7 mt-1">Cron geçerli değil. Örnek: 0 0 6 1 * ? (her ayın 1'i 06:00)</div>}

                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        {
                                            CRON_SABLONLARI.map(s => (
                                                <button key={s.value} type="button" className={"btn btn-sm " + (cron?.trim() == s.value ? 'btn-primary' : 'btn-light-primary')} onClick={() => {
                                                    setCron(s.value);
                                                    // Hazir zamanlama donemi de ayarlar (her pazartesi -> onceki hafta); sabit gelir hep onceki ay.
                                                    if (!nakliye)
                                                        setDonem(secenek(DONEMLER, s.donem));
                                                }}>{s.label}</button>
                                            ))
                                        }
                                    </div>
                                </div>

                                <div className="col-md-7">
                                    <label className="form-label">Sonraki 5 çalışma</label>
                                    <div className="fs-7 text-gray-700" style={{ fontFamily: 'monospace' }}>
                                        {cronOnizleme.zamanlar.length == 0 ? <span className="text-muted">—</span> : cronOnizleme.zamanlar.map(z => <div key={z}>{z}</div>)}
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-3">
                                    <CustomDatePicker labelText="Geçerlilik Başlangıcı" isLabelRequired value={baslangic} setValue={setBaslangic} />
                                </div>

                                <div className="col-md-3">
                                    <CustomDatePicker labelText="Geçerlilik Bitişi (boş = süresiz)" value={bitis} setValue={setBitis} />
                                </div>

                                <div className="col-md-3">
                                    <CustomSingleSelect
                                        labelText="Faturalanacak Dönem"
                                        isLabelRequired
                                        disabled={nakliye}
                                        selectedValue={donem}
                                        onChange={(v) => setDonem(v ?? secenek(DONEMLER, 1))}
                                        onMenuOpen={secenekler(DONEMLER)}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>

                                <div className="col-md-3">
                                    <CustomSingleSelect
                                        labelText="Fatura Tarihi"
                                        isLabelRequired
                                        selectedValue={faturaTarihi}
                                        onChange={(v) => setFaturaTarihi(v ?? secenek(FATURA_TARIHLERI, 1))}
                                        onMenuOpen={secenekler(FATURA_TARIHLERI)}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-5">
                                Örnek: her ayın 1'i 06:00'da çalışan kural, "Önceki ay" ile 1–30 Kasım'ı 1 Aralık'ta faturalar. Her pazartesi 08:00 + "Önceki hafta" bir önceki pazartesi–pazar işlerini faturalar. Aynı dönem iki kez faturalanmaz.
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Kapsam</h4>

                            <div className="row">
                                <div className="col-md-8">
                                    <CustomMultiSelect
                                        labelText="Müşteriler (boş = tümü)"
                                        selectedValues={musteriler}
                                        onChange={setMusteriler}
                                        onMenuOpen={() => CompanyService.getCompanyList({ isCustomer: true })}
                                        labelName='name'
                                        valueName='companyId'
                                    />
                                </div>

                                {
                                    nakliye ? (
                                        <div className="col-md-4">
                                            <CustomSingleSelect
                                                labelText="Gelir Türü"
                                                selectedValue={sabitGelirTuru}
                                                onChange={(v) => setSabitGelirTuru(v ?? secenek(SABIT_GELIR_SECENEKLERI, ''))}
                                                onMenuOpen={secenekler(SABIT_GELIR_SECENEKLERI)}
                                                labelName='label'
                                                valueName='value'
                                            />
                                        </div>
                                    ) : (
                                        <div className="col-md-4">
                                            <label className="form-label">Faturaya Gruplama (Müşteri her zaman)</label>
                                            <div className="d-flex gap-5 mt-2">
                                                {
                                                    GRUPLAMA_ALANLARI.map(g => (
                                                        <label key={g.value} className="form-check form-check-sm form-check-custom form-check-solid">
                                                            <input className="form-check-input" type="checkbox" checked={gruplama.includes(g.value)}
                                                                onChange={() => setGruplama(gruplama.includes(g.value) ? gruplama.filter(x => x != g.value) : [...gruplama, g.value])} />
                                                            <span className="form-check-label">{g.label}</span>
                                                        </label>
                                                    ))
                                                }
                                            </div>

                                            <label className="form-check form-check-sm form-check-custom form-check-solid mt-4">
                                                <input className="form-check-input" type="checkbox" checked={hizmetGrupla} onChange={() => setHizmetGrupla(!hizmetGrupla)} />
                                                <span className="form-check-label">Fatura satırlarını hizmet kalemine göre grupla</span>
                                            </label>

                                            <label className="form-check form-check-sm form-check-custom form-check-solid mt-4">
                                                <input id="otofatura-ay-sonunda-bol" className="form-check-input" type="checkbox" checked={aySonundaBol} onChange={() => setAySonundaBol(!aySonundaBol)} />
                                                <span className="form-check-label">Ay sonunda faturayı böl</span>
                                            </label>
                                            <div className="text-muted fs-8 mt-1 ms-7">
                                                Hafta iki aya yayılırsa (örn. 30 Kasım – 6 Aralık) her ay ayrı faturalanır; önceki ayın faturası ayın son günü tarihli kesilir.
                                            </div>
                                        </div>
                                    )
                                }
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-8">
                                    <CustomMultiSelect
                                        labelText={`PO Numaraları (boş = tümü; PO Eşleştirme · ${tur == 2 ? 'Yurtiçi Kargo' : tur == 3 ? 'Nakliye' : 'Depo Kira ve Personel'} projesi)`}
                                        selectedValues={poNumaralari}
                                        onChange={setPoNumaralari}
                                        onMenuOpen={() => OtomatikFaturaService.poNumaralari(tur, musteriler.map(x => x.value))}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-5">
                                {tur == 3 && <div className="mb-2"><b>Nakliye dönemsel:</b> aylık sabit gelir PO'larını seçmeyin (kira / personel sabit gelir kuralıyla faturalanır). Seçilse bile sabit gelir siparişleri ve sabit gelir hizmetli satırlar bu kurala girmez.</div>}
                                {
                                    nakliye
                                        ? 'Sabit Gelir Dağıtımı kartlarındaki her müşteri + PO No + gelir türü bir sipariş ve bir fatura olur (Siparişler > "Sabit gelirleri getir → Siparişi oluştur" ile aynı hesap). Dönemin siparişi zaten varsa o faturalanır.'
                                        : `Dönem içindeki faturalanmamış beklenen gelir satırları gruplanır; her grup bir fatura olur. Bugünkü pratik: Müşteri + PO No (müşterinin PO Eşleştirme'deki "${projeAdi}" projesi PO numaraları; her PO numarası için bir fatura, bir faturada birden çok bölüm). PO No'su boş ya da bu listede olmayan ${birim} ve beklenen geliri olmayan ${birim} faturaya girmez, raporda listelenir.${tur == 3 ? ' Aylık sabit gelir siparişleri bu kurala girmez. Tevkifata tabi müşteride tevkifatlı ve tevkifatsız hizmetler ayrı faturada kesilir.' : ''} Hizmet gruplaması açıksa fatura satırları Faturalar ekranındaki "Grupla" gibi hizmet kalemi bazında birleşir. Aynı anda çalışan kurallarda önce Nakliye, sonra Yurtiçi Kargo faturalanır.`
                                }
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Gönderim</h4>

                            <div className="row">
                                {
                                    GONDERIMLER.map(g => (
                                        <div key={g.value} className="col-md-4 mb-3">
                                            <label className={"d-flex gap-3 border rounded p-4 h-100 cursor-pointer " + (gonderim == g.value ? 'border-primary bg-light-primary' : '')}>
                                                <input className="form-check-input mt-1" type="radio" name="gonderim" checked={gonderim == g.value} onChange={() => setGonderim(g.value)} />
                                                <span>
                                                    <span className="fw-bold d-block">{g.label}</span>
                                                    <span className="text-muted fs-7">{g.aciklama}</span>
                                                </span>
                                            </label>
                                        </div>
                                    ))
                                }
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Onay ve Bildirim</h4>

                            <div className="row">
                                <div className="col-md-2">
                                    <label className="form-label">Onaylı</label>
                                    <CustomSwitch isActive={onayGerekli} setIsActive={setOnayGerekli} />
                                </div>

                                {
                                    onayGerekli && (
                                        <div className="col-md-5">
                                            <CustomSingleSelect
                                                labelText="Onaylayıcı (Kullanıcı Tanımları, e-postası olan)"
                                                isLabelRequired
                                                selectedValue={onaylayici}
                                                onChange={setOnaylayici}
                                                onMenuOpen={() => OtomatikFaturaService.onaylayicilar()}
                                                labelName='label'
                                                valueName='value'
                                            />
                                        </div>
                                    )
                                }
                            </div>

                            {
                                onayGerekli && (
                                    <React.Fragment>
                                        <div className="row mt-5">
                                            <div className="col-md-3">
                                                <CustomNumberInput labelText="Hatırlatma Aralığı (dakika)" value={hatirlatmaDakika} setValue={setHatirlatmaDakika} />
                                            </div>
                                            <div className="col-md-3">
                                                <CustomNumberInput labelText="Hatırlatma Başlangıç Saati" value={hatirlatmaBas} setValue={setHatirlatmaBas} />
                                            </div>
                                            <div className="col-md-3">
                                                <CustomNumberInput labelText="Hatırlatma Bitiş Saati" value={hatirlatmaBit} setValue={setHatirlatmaBit} />
                                            </div>
                                        </div>

                                        <div className="text-muted fs-7 mt-3">
                                            Onay maili faturaların özetini, Excel dökümünü ve Uyumsoft taslaklarının PDF'lerini içerir; onay maildeki düğmeyle verilir (Keylogx'e giriş gerekmez).
                                            Onaylanana ya da reddedilene kadar belirtilen saatler arasında hatırlatma gider; her hatırlatmada yeni bağlantı gönderilir, eski bağlantılar geçersiz olur.
                                            Reddedilirse faturalar ve Uyumsoft taslakları aynı numarayla kalır.
                                        </div>
                                    </React.Fragment>
                                )
                            }

                            <div className="row mt-5">
                                <div className="col-md-8">
                                    <MailListesiAlani labelText="Bilgi Alıcıları (sonuç ve hata maili; onaylayıcıya ayrıca gider)" value={bilgiAlicilari} setValue={setBilgiAlicilari} rows={2} />
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <div className="p-4 rounded bg-light-primary fs-6 mb-5">{ozet()}</div>

                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light-primary" onClick={onizlemeyiAc}>Önizle / Şimdi Çalıştır</button>
                                </Box>
                                <Box>
                                    <button className="btn btn-primary btn-sm" onClick={kaydet}>Kaydet</button>
                                </Box>
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>

            {
                kuralId && (
                    <OnizlemeModal
                        isShown={onizlemeAcik}
                        kuralId={kuralId}
                        kuralAdi={ad ?? ''}
                        onClose={() => setOnizlemeAcik(false)}
                        onCalisti={() => navigate('/otomatikfatura/gecmis?kuralId=' + kuralId)}
                    />
                )
            }
        </React.Fragment>
    )
}

export default OtomatikFaturaAdd;
